import {
  normalizedPaths,
  normalizedPointToPage,
  pagePointToNormalized,
  polygonBoolean,
} from './layoutGraphicGeometry';
import type {
  LayoutGraphicElement,
  LayoutGraphicGeometry,
  LayoutGraphicPoint,
  PageRect,
} from './layoutTypes';

function bounds(points: readonly LayoutGraphicPoint[]): PageRect | null {
  if (!points.length) return null;
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    width: Math.max(1, Math.max(...xs) - x),
    height: Math.max(1, Math.max(...ys) - y),
  };
}

export function graphicGeometryInPageSpace(
  graphic: LayoutGraphicElement,
): LayoutGraphicGeometry {
  return {
    type: 'path',
    paths: normalizedPaths(graphic.geometry).map((path) => ({
      ...path,
      points: path.points.map((point) => normalizedPointToPage(point, graphic.rect)),
    })),
  };
}

export function fitPageGeometry(
  geometry: LayoutGraphicGeometry,
): { readonly rect: PageRect; readonly geometry: LayoutGraphicGeometry } | null {
  const paths = geometry.type === 'path' ? geometry.paths : normalizedPaths(geometry);
  const rect = bounds(paths.flatMap((path) => path.points));
  if (!rect) return null;
  return {
    rect,
    geometry: {
      type: 'path',
      paths: paths.map((path) => ({
        ...path,
        points: path.points.map((point) => pagePointToNormalized(point, rect)),
      })),
    },
  };
}

export function combinePagePolygons(
  operation: 'union' | 'intersection' | 'difference' | 'xor',
  graphics: readonly LayoutGraphicElement[],
): { readonly rect: PageRect; readonly geometry: LayoutGraphicGeometry } | null {
  if (graphics.length < 2) return null;
  const group = bounds(graphics.flatMap((graphic) =>
    normalizedPaths(graphic.geometry).flatMap((path) =>
      path.points.map((point) => normalizedPointToPage(point, graphic.rect)))));
  if (!group) return null;
  const normalized = graphics.map((graphic) => ({
    type: 'path' as const,
    paths: normalizedPaths(graphic.geometry).map((path) => ({
      ...path,
      points: path.points.map((point) =>
        pagePointToNormalized(normalizedPointToPage(point, graphic.rect), group)),
    })),
  }));
  const [first, ...rest] = normalized;
  const combined = polygonBoolean(operation, first, ...rest);
  const pageGeometry: LayoutGraphicGeometry = {
    type: 'path',
    paths: normalizedPaths(combined).map((path) => ({
      ...path,
      points: path.points.map((point) => normalizedPointToPage(point, group)),
    })),
  };
  return fitPageGeometry(pageGeometry);
}
